import type { Ticket, TicketVoidNotice } from '@gatecheckpro/contracts';
import {
  Notice,
  TICKET_VOID_REASONS,
  formatMoney,
  presentation,
} from '@gatecheckpro/design-system';

/**
 * Whether a ticket can still get somebody through a gate, and how to say so.
 *
 * The rule this file exists to hold: **a ticket that is no longer valid must
 * never be presented as a usable pass, and must never disappear.** Those pull in
 * opposite directions and both matter. Showing a void ticket as live sends
 * somebody to a venue to be turned away at the door, in front of a queue.
 * Deleting it destroys the only record they have that they bought anything —
 * which is exactly the record they need when they are asking where their money
 * went.
 *
 * So a void ticket stays on the list, keeps its number, its event, its seat and
 * its purchase history, and loses only the thing it can no longer do.
 *
 * The client decides none of this. `ticket.status` is the server's answer and
 * the only input; there is no date arithmetic here, no inference from an order
 * state, and nothing that reads a refund and concludes a ticket must be void.
 */
export function isVoid(ticket: Ticket): boolean {
  return ticket.status === 'VOID';
}

/**
 * What a customer is told about a ticket that no longer works.
 *
 * Deliberately quiet about money. Whether any went back is a separate fact the
 * server states separately, and a ticket can be voided with nothing to refund —
 * a free ticket has no payment to reverse. Reading "refunded" off the void
 * alone would promise a payment that never happened, which is the same class of
 * lie the zero-total work exists to prevent, arriving from the other direction.
 *
 * Until the server carries a reason on the ticket read, this says the true
 * smaller thing: the ticket cannot be used, and where to ask why. It does not
 * guess a reason, and it does not name an amount.
 */
export function VoidNotice({
  ticket,
  /*
   * The server's account of why, when it has one.
   *
   * Defaults to the ticket's own `voidNotice`, which the customer read now
   * carries. It stays overridable because the component was written before that
   * field existed and rendered the true smaller thing without it — a surface
   * that had asserted the field early would have been the client inventing an
   * API shape, and the degraded path is still the honest answer for any caller
   * holding a ticket read from an older response.
   */
  notice,
}: {
  ticket: Ticket;
  notice?: TicketVoidNotice | null;
}): React.JSX.Element | null {
  if (!isVoid(ticket)) return null;

  const shown = notice === undefined ? ticket.voidNotice : notice;
  const reason = shown === null ? null : presentation(TICKET_VOID_REASONS, shown.reason);

  return (
    // Wrapped rather than passing a test id into `Notice`: the shared component
    // takes a closed prop set on purpose, and widening it for one surface's
    // convenience would be the wrong end of the trade.
    <div data-testid={`ticket-void-${ticket.ticketId}`}>
      <Notice tone="caution" title={reason?.label ?? 'This ticket can no longer be used'}>
        <span>
          {reason?.meaning ??
            'This ticket is no longer valid, so it will not get you in. Your purchase record below stays as it is.'}
        </span>
        {/*
          An amount, only when money actually went back.

          `refundedAmount` is null whenever nothing has returned — and that
          covers two situations this component cannot tell apart: a free ticket
          that never had a payment to reverse, and a paid one whose refund was
          approved but has not settled. An earlier version rendered "nothing was
          charged for it, so there is nothing to refund" for both, which told
          somebody who had paid ₦40,000 and was waiting for it back that they had
          never paid anything. A refund journey caught it.

          So the null case says nothing about money at all. The reason above
          already carries the meaning, and silence is the only sentence that is
          true in both situations.
        */}
        {shown !== null && shown.refundedAmount !== null && (
          <span> {formatMoney(shown.refundedAmount)} was refunded.</span>
        )}
      </Notice>
    </div>
  );
}
